/**
 * 定盘 → 完整报告 流程容器
 *
 * 三态：
 *   1. loading：正在生成定盘卡片
 *   2. choosing：展示卡片，等待用户选择
 *   3. report：完整报告流式生成（含付费墙）
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { CalibrationCandidate } from '@guanwei/shared';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { generateCalibration, selectCalibration } from '@/lib/calibration.api';
import { CalibrationCards } from '@/components/calibration/CalibrationCards';
import { FullReportPanel } from '@/components/chart/FullReportPanel';

interface CalibrationFlowProps {
  chartId: string;
  onReportStart?: () => void;
  onReportDone?: (reportId?: string, content?: string) => void;
}

type Stage = 'loading' | 'choosing' | 'selecting' | 'report' | 'error';

interface CalibrationState {
  calibrationId: string;
  candidates: CalibrationCandidate[];
  fallback: boolean;
}

export function CalibrationFlow({ chartId, onReportStart, onReportDone }: CalibrationFlowProps) {
  const [stage, setStage] = useState<Stage>('loading');
  const [state, setState] = useState<CalibrationState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setStage('loading');
    setError(null);
    void generateCalibration(chartId)
      .then((resp) => {
        if (!alive) return;
        setState({
          calibrationId: resp.calibrationId,
          candidates: resp.candidates,
          fallback: resp.fallback,
        });
        setStage('choosing');
      })
      .catch((err: Error) => {
        if (!alive) return;
        setError(err.message);
        setStage('error');
      });
    return () => {
      alive = false;
    };
  }, [chartId]);

  const handleSelect = async (schoolId: string) => {
    if (!state) return;
    setError(null);
    setStage('selecting');
    try {
      const resp = await selectCalibration(state.calibrationId, schoolId);
      setConfirmMessage(resp.confirmMessage);
      setStage('report');
    } catch (err) {
      setError((err as Error).message);
      setStage('error');
    }
  };

  if (stage === 'loading') {
    return (
      <Card className="p-12 text-center">
        <p className="font-serif text-xl text-fg mb-2">观微先生正在审视你的命盘…</p>
        <p className="text-sm text-fg-2">即将给出 2-3 个判定流派的对照视角</p>
        <div className="mt-6 inline-flex items-center gap-2 text-accent">
          <span className="inline-block w-2 h-2 bg-accent rounded-full animate-pulse" />
          <span className="inline-block w-2 h-2 bg-accent rounded-full animate-pulse [animation-delay:0.2s]" />
          <span className="inline-block w-2 h-2 bg-accent rounded-full animate-pulse [animation-delay:0.4s]" />
        </div>
      </Card>
    );
  }

  if (stage === 'error') {
    return (
      <Card className="p-8 text-center">
        <p className="font-serif text-xl text-danger mb-2">定盘失败</p>
        <p className="text-sm text-fg-2 mb-4">{error}</p>
        <div className="flex justify-center gap-3">
          {state?.candidates.length ? (
            <Button variant="outline" onClick={() => setStage('choosing')}>
              返回重选
            </Button>
          ) : null}
          <Button onClick={() => window.location.reload()}>重试</Button>
        </div>
      </Card>
    );
  }

  if (stage === 'choosing' && state) {
    return (
      <CalibrationCards
        candidates={state.candidates}
        fallback={state.fallback}
        onSelect={handleSelect}
      />
    );
  }

  if (stage === 'selecting') {
    return (
      <Card variant="gold" className="p-12 text-center">
        <p className="text-xs tracking-[0.3em] text-accent mb-3">CALIBRATION LOCKED</p>
        <p className="font-serif text-2xl text-fg">判定路径已锁定</p>
        <p className="mt-2 text-sm text-fg-2">观微先生正在按该流派生成完整报告</p>
        <div className="mx-auto mt-6 h-1 w-40 overflow-hidden rounded-pill bg-bg-2">
          <motion.div
            className="h-full rounded-pill bg-accent"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.8 }}
          />
        </div>
      </Card>
    );
  }

  if (stage === 'report' && state) {
    return (
      <div className="space-y-4">
        {confirmMessage && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-border-accent bg-accent-soft p-5 text-sm leading-relaxed text-fg-1 shadow-gold whitespace-pre-wrap"
          >
            <p className="mb-2 font-serif text-lg text-accent">定盘确认</p>
            {confirmMessage}
          </motion.div>
        )}
        <FullReportPanel
          chartId={chartId}
          calibrationId={state.calibrationId}
          onReportStart={onReportStart}
          onReportDone={onReportDone}
        />
      </div>
    );
  }

  return null;
}
